import React from 'react';
import { Modal, Stack, Text, Checkbox, Button, Group, Alert, Textarea } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface IPatientAcknowledgmentModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (note?: string) => void;
    isLoading?: boolean;
}

export const PatientAcknowledgmentModal: React.FC<IPatientAcknowledgmentModalProps> = ({
    opened,
    onClose,
    onConfirm,
    isLoading,
}) => {
    const { t } = useTranslation();
    const [acknowledged, setAcknowledged] = React.useState(false);
    const [note, setNote] = React.useState('');

    // Reset state when modal closes
    React.useEffect(() => {
        if (!opened) {
            setAcknowledged(false);
            setNote('');
        }
    }, [opened]);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Text fw={600}>
                    {t('otp.patientAcknowledgment.modal.title', 'Patient Acknowledgment')}
                </Text>
            }
            size="md"
            closeOnClickOutside={false}
        >
            <Stack gap="md">
                <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
                    <Text size="sm">
                        {t(
                            'otp.patientAcknowledgment.modal.description',
                            'Before activating this tapering plan, please confirm that the patient has been informed and agrees with the plan.'
                        )}
                    </Text>
                </Alert>

                <Checkbox
                    label={
                        <Text size="sm" fw={500}>
                            {t(
                                'otp.patientAcknowledgment.modal.checkbox',
                                'The patient has reviewed the tapering plan and agrees to proceed'
                            )}
                        </Text>
                    }
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.currentTarget.checked)}
                />

                <Textarea
                    label={t('otp.patientAcknowledgment.modal.noteLabel', 'Note')}
                    placeholder={t('otp.patientAcknowledgment.modal.notePlaceholder', 'Add an optional note...')}
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    minRows={3}
                    maxRows={5}
                    maxLength={1000}
                    autosize
                />

                <Group justify="flex-end" mt="md">
                    <Button variant="outline" color="gray" onClick={onClose}>
                        {t('button.cancel', 'Cancel')}
                    </Button>
                    <Button
                        color="green"
                        onClick={() => onConfirm(note.trim() || undefined)}
                        disabled={!acknowledged}
                        loading={isLoading}
                    >
                        {t('otp.patientAcknowledgment.modal.confirm', 'Activate Plan')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
